import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { Spinner } from '../Spinner/Spinner';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'gold' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: IconKind;
  iconLeft?: IconKind;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    className,
    disabled,
    icon,
    iconLeft,
    loading,
    size = 'md',
    variant = 'primary',
    ...props
  },
  ref,
) {
  const isDisabled = disabled === true || loading === true;

  return (
    <button
      ref={ref}
      {...props}
      disabled={isDisabled}
      className={cn(
        // Design system rev 1: buttons are radius-md (not pills), flat (no hover
        // lift), one weight. The fill is what changes; brand-indigo is primary.
        'cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--r-md)] border font-semibold transition duration-[var(--dur-fast)] disabled:cursor-not-allowed disabled:opacity-40',
        size === 'sm' && 'px-[15px] py-2 text-[13px]',
        size === 'md' && 'px-5 py-[11px] text-sm',
        size === 'lg' && 'px-7 py-3.5 text-[15px]',
        variant === 'primary' &&
          'border-[var(--brand-indigo)] bg-[var(--brand-indigo)] text-[var(--text-on-accent)] shadow-[var(--shadow-nav-active)]',
        variant === 'secondary' &&
          'border-[var(--border-strong)] bg-[var(--surface-card)] text-[var(--text-secondary)] hover:bg-[var(--surface-header)]',
        variant === 'ghost' && 'border-transparent bg-transparent text-[var(--text-secondary)] hover:bg-[var(--surface-header)]',
        variant === 'gold' &&
          'border-[var(--warn-amber-bar)] bg-[var(--warn-amber-bar)] text-[var(--text-primary)]',
        variant === 'danger' &&
          'border-[var(--alert-red-border)] bg-[var(--surface-card)] text-[var(--alert-red)] hover:bg-[var(--alert-red-tint)]',
        className
      )}
    >
      {loading ? (
        <Spinner
          size={14}
          color={variant === 'primary' ? '#fff' : 'var(--text-secondary)'}
        />
      ) : iconLeft ? (
        <Icon kind={iconLeft} size={15} stroke={2} />
      ) : null}
      {children}
      {icon ? <Icon kind={icon} size={15} stroke={2} /> : null}
    </button>
  );
});
